import { T } from '@/i18n';
import { type InputHTMLAttributes, useEffect, useId, useRef, useState } from 'react';
import { Icon } from './icons';

/**
 * A complete clock time, «HH:MM», from what was typed: 13:45, 7:30, 13.45, 1345 or 930 (09:30, #272 A-T04).
 * Anything shorter («1», «17:3», «7:5») or out of range («25:00») is not a time yet and reads as null
 * (#272 A-S01, A-S02).
 */
export function readTime(text: string): string | null {
  const m = /^(\d{1,2})[:.](\d{2})$/.exec(text.trim()) ?? /^(\d{1,2})(\d{2})$/.exec(text.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

/**
 * A clock time as the app writes it, «13:45», in the same frame as the app's date field (DateInput), not the
 * browser's own field with its «--:--» (#272 A-13). The typed text stays in the field: the owner is handed
 * only a complete time, or '' when the field is emptied, never a half-typed one, so hours, headers and saves
 * keep the last good value (#272 A-S01, A-S02). `onInvalidChange` says whether the text is unfinished or
 * wrong, so the owner can hold its save; the reason shows under the field once it is left.
 */
export function TimeInput({
  value,
  onValueChange,
  onInvalidChange,
  className,
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> & {
  value: string;
  onValueChange: (value: string) => void;
  onInvalidChange?: ((invalid: boolean) => void) | undefined;
}) {
  const [text, setText] = useState(value);
  const [left, setLeft] = useState(false);
  const msgId = useId();
  // a value from outside (Fyll inn, another shift) is shown; text being typed that the value echoes is kept
  useEffect(() => {
    setText((t) => ((readTime(t) ?? t) === value ? t : value));
  }, [value]);
  const wrong = text.trim() !== '' && readTime(text) === null;
  const report = useRef(onInvalidChange);
  report.current = onInvalidChange;
  useEffect(() => {
    report.current?.(wrong);
  }, [wrong]);
  // a field that goes away holds nothing back
  useEffect(() => () => report.current?.(false), []);
  const shown = wrong && left;
  // a shift that ends at midnight is saved as 00:00, never 24:00 (#272 A-T04)
  const midnight = /^24([:.]?00)?$/.test(text.trim());
  const describedBy =
    [props['aria-describedby'], shown ? msgId : null].filter(Boolean).join(' ') || undefined;
  return (
    <>
      <span className="date-field time-field">
        <input
          {...props}
          className={className}
          data-time=""
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder={T('tt:mm')}
          aria-invalid={props['aria-invalid'] ?? (shown || undefined)}
          aria-describedby={describedBy}
          value={text}
          onChange={(e) => {
            const t = e.target.value;
            setText(t);
            if (t.trim() === '') onValueChange('');
            else {
              const v = readTime(t);
              if (v) onValueChange(v);
            }
          }}
          onBlur={(e) => {
            props.onBlur?.(e);
            setLeft(true);
            const t = readTime(e.currentTarget.value);
            if (t) setText(t);
          }}
        />
        <span className="tf-ic" aria-hidden="true">
          <Icon name="clock" />
        </span>
      </span>
      {shown && (
        <span className="verr" id={msgId} role="alert" data-testid="time-error">
          {midnight ? T('Midnatt skrives 00:00.') : T('Skriv klokkeslettet som tt:mm, for eksempel 07:30.')}
        </span>
      )}
    </>
  );
}
